import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { getMyStores, getChannelsByStore } from '../../api/stores';
import { getCallLogs } from '../../api/callLogs';
import { getReservations } from '../../api/reservations';

const MAX_MINUTES = 300;

const OverviewPage = () => {
    const now = new Date();
    const 년 = now.getFullYear();
    const 월 = now.getMonth();
    const startDate = `${년}-${String(월 + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(년, 월 + 1, 0).getDate();
    const endDate = `${년}-${String(월 + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    const monthStart = new Date(년, 월, 1);
    const monthEnd = new Date(년, 월 + 1, 0, 23, 59, 59);

    const [로딩중, set로딩중] = useState(true);
    const [통화건수, set통화건수] = useState(0);
    const [통화분수, set통화분수] = useState(0);
    const [예약건수, set예약건수] = useState(0);

    useEffect(() => {
        const load = async () => {
            const stores = await getMyStores();
            if (!stores || stores.length === 0) return;

            const callLogsResults = await Promise.allSettled(
                stores.map((s) => getCallLogs(s.uuid))
            );

            let totalCalls = 0;
            let totalSeconds = 0;
            callLogsResults.forEach((result) => {
                if (result.status !== 'fulfilled') return;
                const logs = Array.isArray(result.value?.call_logs) ? result.value.call_logs : [];
                const thisMonth = logs.filter((log) => {
                    const d = new Date(log.started_at);
                    return d >= monthStart && d <= monthEnd;
                });
                totalCalls += thisMonth.length;
                totalSeconds += thisMonth.reduce((sum, l) => sum + (l.duration_sec ?? 0), 0);
            });
            set통화건수(totalCalls);
            set통화분수(Math.round(totalSeconds / 60));

            const channelsResults = await Promise.allSettled(
                stores.map((s) =>
                    getChannelsByStore(s.uuid).then((ch) => ({ storeUuid: s.uuid, channels: ch }))
                )
            );

            const reservationFetches = [];
            channelsResults.forEach((result) => {
                if (result.status !== 'fulfilled') return;
                const { storeUuid, channels } = result.value;
                if (!Array.isArray(channels)) return;
                channels.forEach((ch) => {
                    reservationFetches.push(
                        getReservations(storeUuid, ch.uuid, startDate, endDate)
                    );
                });
            });

            const reservationResults = await Promise.allSettled(reservationFetches);
            let totalReservations = 0;
            reservationResults.forEach((result) => {
                if (result.status !== 'fulfilled') return;
                const events = Array.isArray(result.value?.events) ? result.value.events : [];
                totalReservations += events.length;
            });
            set예약건수(totalReservations);
        };

        load()
            .catch(() => {})
            .finally(() => set로딩중(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const usagePercent = Math.min((통화분수 / MAX_MINUTES) * 100, 100);
    const remaining = Math.max(MAX_MINUTES - 통화분수, 0);

    return (
        <PageWrapper>
            <Contents>
                <TitleRow>
                    <PageTitle>이달의 현황</PageTitle>
                    <MonthChip>{년}년 {월 + 1}월</MonthChip>
                </TitleRow>

                {로딩중 ? (
                    <StatusText>불러오는 중...</StatusText>
                ) : (
                    <StatGrid>
                        <StatCard>
                            <StatLabel>통화 처리</StatLabel>
                            <StatValueRow>
                                <StatValue>{통화건수.toLocaleString()}</StatValue>
                                <StatUnit>건</StatUnit>
                            </StatValueRow>
                            <StatDesc>AI가 응대한 전화 건수</StatDesc>
                        </StatCard>

                        <StatCard>
                            <StatLabel>통화 사용 시간</StatLabel>
                            <StatValueRow>
                                <StatValue>{통화분수.toLocaleString()}</StatValue>
                                <StatUnit>분 / {MAX_MINUTES}분</StatUnit>
                            </StatValueRow>
                            <ProgressBarWrap>
                                <ProgressBarFill $percent={usagePercent} $warn={usagePercent >= 80} />
                            </ProgressBarWrap>
                            <StatDesc>잔여 {remaining}분</StatDesc>
                        </StatCard>

                        <StatCard>
                            <StatLabel>AI 예약</StatLabel>
                            <StatValueRow>
                                <StatValue>{예약건수.toLocaleString()}</StatValue>
                                <StatUnit>건</StatUnit>
                            </StatValueRow>
                            <StatDesc>이번 달 AI가 접수한 예약</StatDesc>
                        </StatCard>
                    </StatGrid>
                )}
            </Contents>
        </PageWrapper>
    );
};


const PageWrapper = styled.div`
    width: 100%;
    min-height: 100dvh;
    background: #f7f8fa;
`;

const Contents = styled.div`
    max-width: 960px;
    margin: 0 auto;
    padding: 80px 16px 48px;
`;

const TitleRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 32px;
`;

const PageTitle = styled.h1`
    font-family: Pretendard;
    font-size: 24px;
    font-weight: 700;
    color: #111111;
    margin: 0;
`;

const MonthChip = styled.span`
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 500;
    color: #487BFD;
    background: #eef2ff;
    border-radius: 20px;
    padding: 5px 14px;
`;

const StatGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const StatCard = styled.div`
    background: #ffffff;
    border: 1px solid #eeeeee;
    border-radius: 16px;
    padding: 28px 24px 24px;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const StatLabel = styled.span`
    font-family: Pretendard;
    font-size: 11px;
    font-weight: 600;
    color: #aaaaaa;
    text-transform: uppercase;
    letter-spacing: 0.08em;
`;

const StatValueRow = styled.div`
    display: flex;
    align-items: baseline;
    gap: 6px;
`;

const StatValue = styled.span`
    font-family: Pretendard;
    font-size: 40px;
    font-weight: 700;
    color: #111111;
    line-height: 1;
`;

const StatUnit = styled.span`
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 500;
    color: #777777;
`;

const ProgressBarWrap = styled.div`
    width: 100%;
    height: 6px;
    background: #f0f0f0;
    border-radius: 3px;
    overflow: hidden;
`;

const ProgressBarFill = styled.div`
    height: 100%;
    width: ${({ $percent }) => $percent}%;
    background: ${({ $warn }) => $warn ? '#f59e0b' : '#487BFD'};
    border-radius: 3px;
    transition: width 0.4s ease;
`;

const StatDesc = styled.span`
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 400;
    color: #aaaaaa;
`;

const StatusText = styled.span`
    display: block;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    color: #aaaaaa;
`;


export default OverviewPage;
